import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ENDPOINT } from 'constants/variables';
import { helpHttp } from 'service/HelpHttp';
import { warningErrorAction } from 'actions/ErrorActions';

const STATUS_LABELS = {
  ACTIVE: 'Vigente', SUPERSEDED: 'Sustituida', FINISHED: 'Finalizada',
  DISABLED: 'Deshabilitada', DRAFT: 'Borrador'
};

const FIELD_LABELS = {
  'formdi.ori_tras_documentid': 'NIF/CIF del origen',
  'formdi.ori_tras_tradename': 'Razón social del origen',
  'formdi.ori_tras_street': 'Dirección del origen',
  'formdi.ori_tras_nima': 'NIMA del origen',
  'formdi.dest_tras_documentid': 'NIF/CIF del destino',
  'formdi.dest_tras_tradename': 'Razón social del destino',
  'formdi.dest_tras_street': 'Dirección del destino',
  'formdi.dest_tras_nima': 'NIMA del destino',
  'formdi.rel_trans_documentid': 'NIF/CIF del transportista',
  'formdi.rel_trans_tradename': 'Transportista',
  'formdi.preiddoc_sheet': 'Prefijo del documento DI',
  'formdi.sufiddoc_sheet': 'Sufijo del documento DI',
  'formdi.start_date_sheet': 'Fecha de inicio del traslado',
  'formdi.end_date_sheet': 'Fecha de finalización del traslado',
  'formdi.linessheet': 'Residuos y cantidades'
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') {
    const text = JSON.stringify(value);
    return text.length > 180 ? `${text.slice(0, 180)}…` : text;
  }
  return String(value);
};

const visibleChanges = (value) => {
  if (!value) return [];
  try {
    const changes = JSON.parse(value);
    return Array.isArray(changes)
      ? changes.filter((change) => FIELD_LABELS[change.field])
      : [];
  } catch (error) {
    return [];
  }
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const fetchWithTimeout = async (url, options = {}, milliseconds = 50000) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), milliseconds);
  try { return await fetch(url, { ...options, signal: controller.signal }); }
  finally { clearTimeout(timeout); }
};

const FormDIDecaViewer = ({ id, onFormDILoaded, onServiceLoaded }) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const [formdi, setFormdi] = useState(null);
  const [service, setService] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [error, setError] = useState(null);

  const loadPdf = useCallback(async (document, loadedService) => {
    setPdfError(false);
    const url = loadedService?.shared
      ? `${ENDPOINT}/deca/documents/formdi/${id}/document/${document.globalDocumentId}/content`
      : `${ENDPOINT}/deca/documents/public/${document.publicToken}/content`;
    if (loadedService?.shared && !document.globalDocumentId) throw new Error('document-id');
    const response = await fetchWithTimeout(url, {
      headers: loadedService?.shared ? { Authorization: `Bearer ${token}` } : {}
    });
    if (!response.ok) throw new Error('document-content');
    const blob = await response.blob();
    setPdfUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return URL.createObjectURL(blob);
    });
  }, [id, token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const formdiData = await helpHttp().get2(ENDPOINT, 'form/getformdibyId/', token, id);
        let serviceData;
        let activeDocument;
        let documentHistory;
        for (let attempt = 0; attempt < 15; attempt += 1) {
          try {
            serviceData = await helpHttp().get(ENDPOINT, `deca/transport-services/formdi/${id}`, token);
            if (serviceData?.shared) {
              activeDocument = await helpHttp().get(
                ENDPOINT, `deca/documents/formdi/${id}/active`, token
              );
              documentHistory = await helpHttp().get(
                ENDPOINT, `deca/documents/formdi/${id}/history`, token
              );
              activeDocument = documentHistory?.find((item) => item.status === 'ACTIVE')
                || documentHistory?.[0] || activeDocument;
              if (activeDocument?.publicToken) break;
            } else if (serviceData?.id) {
              [activeDocument, documentHistory] = await Promise.all([
                helpHttp().get(ENDPOINT, `deca/documents/service/${serviceData.id}/active`, token),
                helpHttp().get(ENDPOINT, `deca/documents/formdi/${id}/history`, token)
              ]);
              if (activeDocument?.publicToken) break;
            }
          } catch (retryError) {
            // La generación del DeCA del DI puede seguir ejecutándose en segundo plano.
          }
          await wait(1000);
        }
        if (!formdiData || !serviceData || !activeDocument?.publicToken) throw new Error('service');
        if (!cancelled) {
          onFormDILoaded?.(formdiData);
          onServiceLoaded?.(serviceData);
          setFormdi(formdiData);
          setService(serviceData);
          setHistory(Array.isArray(documentHistory) ? documentHistory : []);
          setSelectedDocument(activeDocument);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError('No se pudo abrir el expediente DeCA del DI');
          dispatch(warningErrorAction('No se pudo abrir el expediente DeCA del DI'));
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dispatch, id, onFormDILoaded, onServiceLoaded, token]);

  useEffect(() => {
    if (!selectedDocument) return undefined;
    loadPdf(selectedDocument, service).catch(() => {
      setPdfError(true);
      dispatch(warningErrorAction('No se pudo abrir esta versión del DeCA'));
    });
    return undefined;
  }, [dispatch, loadPdf, selectedDocument, service]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const selectedChanges = useMemo(
    () => visibleChanges(selectedDocument?.changeDetails),
    [selectedDocument]
  );

  if (error) return <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}>{error}</div>;
  if (!formdi || !service || !selectedDocument) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Preparando expediente DeCA del DI…</div>;
  }

  return (
    <div style={{ width: '100%' }}>
      <section className="deca-history-card">
        <div className="deca-history-title">
          <span>Historial y trazabilidad</span>
          <span className="deca-history-count">{history.length} versiones</span>
        </div>
        <div className="deca-version-list">
          {history.map((document) => (
            <button type="button" key={document.id}
              className={`deca-version-button ${selectedDocument.id === document.id ? 'is-selected' : ''}`}
              onClick={() => { setPdfUrl(null); setSelectedDocument(document); }}>
              <strong>Versión {document.version}</strong>
              <span className={`deca-status deca-status-${document.status?.toLowerCase()}`}>
                {STATUS_LABELS[document.status] || document.status}
              </span>
              <small>{document.createdAt ? new Date(document.createdAt).toLocaleString('es-ES') : '—'}</small>
            </button>
          ))}
        </div>
        <div className="deca-trace-summary">
          <div><strong>Usuario:</strong> {selectedDocument.createdByUsername || 'No disponible'}</div>
          <div><strong>Motivo:</strong> {selectedDocument.modificationReason || (selectedDocument.version === 1 ? 'Creación inicial del DeCA del DI' : 'No indicado')}</div>
          <div><strong>Huella:</strong> <code>{selectedDocument.sha256 || '—'}</code></div>
        </div>
        {selectedDocument.version > 1 && (
          <div className="deca-change-list">
            <strong>Cambios respecto a la versión anterior</strong>
            {selectedChanges.length === 0 ? (
              <div className="deca-no-changes">No hay cambios relevantes disponibles para esta versión.</div>
            ) : selectedChanges.map((change, index) => (
              <div className="deca-change-row" key={`${change.field}-${index}`}>
                <span className="deca-change-field">{FIELD_LABELS[change.field]}</span>
                <span className="deca-change-old">{displayValue(change.previousValue)}</span>
                <span className="deca-change-arrow">→</span>
                <span className="deca-change-new">{displayValue(change.newValue)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="deca-pdf-container">
        {!pdfUrl && !pdfError && <div style={{ padding: '2rem', textAlign: 'center' }}>Preparando versión {selectedDocument.version}…</div>}
        {pdfError && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#e74c3c' }}>
            <div>No se pudo recuperar el PDF DeCA desde su servidor responsable.</div>
            <button type="button" onClick={() => loadPdf(selectedDocument, service)}>
              Reintentar
            </button>
          </div>
        )}
        {pdfUrl && <iframe title={`DeCA del DI versión ${selectedDocument.version}`} src={pdfUrl}
          style={{ width: '100%', height: '1180px', border: 0 }} />}
      </div>
    </div>
  );
};

export default FormDIDecaViewer;
